import type { HostInit, WidgetResolution } from '@resto/contracts';
import { HostInitSchema } from '@resto/contracts';
import { MessageBridge, type WindowLike } from './bridge';
import {
  WidgetToHostMessageSchema,
  type HostToWidgetMessage,
  type WidgetToHostMessage,
} from './protocol';

export interface MountWidgetOptions {
  container: HTMLElement;
  resolution: WidgetResolution;
  init: HostInit;
  /** Сколько ждать widget:ready, прежде чем показать ошибку. */
  readyTimeoutMs?: number;
  /** Виджет занимает весь кадр: высоту из widget:resize игнорируем. */
  fill?: boolean;
  onReady?: () => void;
  onResize?: (height: number) => void;
  onToast?: (message: string, level: 'info' | 'success' | 'error') => void;
  onNavigate?: (url: string) => void;
  onPayment?: (
    orderId: string,
    amount: { amount: number; currency: string },
    details: { paymentId?: string; confirmationUrl?: string },
  ) => void;
  onAuthRequired?: (reason: string) => void;
  onError?: (code: string, message: string) => void;
}

export interface MountedWidget {
  iframe: HTMLIFrameElement;
  destroy(): void;
  setTheme(mode: 'light' | 'dark', tokens?: Record<string, string>): void;
  setConfig(config: Record<string, unknown>): void;
  /** Продлить сессию: виджет пересоздаст клиент API с новым токеном. */
  setToken(guestToken: string, expiresAt: string): void;
}

/**
 * Монтирует виджет в iframe и поднимает мост. Инициализация (включая токен гостя)
 * уходит только после widget:ready и только через postMessage.
 */
export function mountWidget(options: MountWidgetOptions): MountedWidget {
  const { container, resolution, init } = options;
  const widgetOrigin = new URL(resolution.htmlUrl).origin;
  const iframe = document.createElement('iframe');
  iframe.className = 'resto-widget-frame';
  iframe.title = init.widgetId;
  iframe.setAttribute(
    'sandbox',
    'allow-scripts allow-forms allow-same-origin allow-popups allow-popups-to-escape-sandbox',
  );
  // Микрофон нужен голосовому вводу: без делегирования iframe получает not-allowed.
  iframe.setAttribute('allow', 'payment; microphone');
  iframe.style.width = '100%';
  iframe.style.border = '0';
  iframe.style.height = '100%';

  const url = new URL(resolution.htmlUrl);
  url.searchParams.set('widget', init.widgetId);
  url.searchParams.set('hostOrigin', window.location.origin);
  iframe.src = url.toString();
  container.appendChild(iframe);

  const bridge = new MessageBridge<HostToWidgetMessage, WidgetToHostMessage>(
    WidgetToHostMessageSchema,
    {
      peerOrigin: widgetOrigin,
      target: iframe.contentWindow as unknown as WindowLike,
      sourceCheck: (event) => event.source === iframe.contentWindow,
      onInvalid: (data) => {
        console.warn('[host] отброшено сообщение виджета', data);
      },
    },
  );

  let ready = false;
  let disposed = false;
  const stopListening = bridge.start();

  const readyTimer = window.setTimeout(() => {
    if (ready || disposed) return;
    options.onError?.('widget_timeout', 'виджет не ответил за отведённое время');
  }, options.readyTimeoutMs ?? 8000);

  bridge.on('widget:ready', () => {
    if (disposed) return;
    ready = true;
    window.clearTimeout(readyTimer);
    const parsedInit = HostInitSchema.parse(init);
    bridge.send({ type: 'host:init', payload: parsedInit });
    options.onReady?.();
  });

  bridge.on('widget:resize', ({ height }) => {
    if (options.fill) {
      iframe.style.height = '100%';
      return;
    }
    iframe.style.height = `${height}px`;
    options.onResize?.(height);
  });

  bridge.on('widget:toast', ({ message, level }) => options.onToast?.(message, level));

  bridge.on('widget:navigate', ({ url: target, external }) => {
    if (external) {
      window.open(target, '_blank', 'noopener,noreferrer');
      return;
    }
    options.onNavigate?.(target);
  });

  bridge.on('widget:payment', ({ orderId, amount, paymentId, confirmationUrl }) =>
    options.onPayment?.(orderId, amount, { paymentId, confirmationUrl }),
  );

  bridge.on('widget:error', ({ code, message }) => options.onError?.(code, message));

  bridge.on('widget:auth-required', ({ reason }) => options.onAuthRequired?.(reason));

  return {
    iframe,
    destroy() {
      disposed = true;
      window.clearTimeout(readyTimer);
      stopListening();
      iframe.remove();
    },
    setTheme(mode, tokens = {}) {
      bridge.send({ type: 'host:theme', payload: { mode, tokens } });
    },
    setConfig(config) {
      bridge.send({ type: 'host:config', payload: { config } });
    },
    setToken(guestToken, expiresAt) {
      bridge.send({ type: 'host:token', payload: { guestToken, expiresAt } });
    },
  };
}
